import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@alphabait/ui' // chart'
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts'
import { BaseQuestionRendererProps } from './types'
import { getChartColors } from '../utils/colors'
import { ChartWrapper, ChartEmptyState } from './shared'
import { useTranslation } from 'react-i18next'

export function BirthdayAgeBucketsBar({ question }: BaseQuestionRendererProps) {
  const { t } = useTranslation()

  // Use aggregated data from backend if available, otherwise process raw answers
  const buckets = question.aggregates?.buckets || []
  const totalResponses = question.totalRespondents || 0

  // If no aggregated data, process the raw answers
  let chartData = buckets
  if (buckets.length === 0 && question.answers.length > 0) {
    // Process raw date answers into age buckets
    const ageBuckets = new Map<string, number>()
    const currentYear = new Date().getFullYear()

    question.answers.forEach((answer) => {
      if (answer.value && typeof answer.value === 'string') {
        const birthDate = new Date(answer.value)
        if (!isNaN(birthDate.getTime())) {
          const age = currentYear - birthDate.getFullYear()
          const bucketStart = Math.floor(age / 10) * 10
          const bucketEnd = bucketStart + 9
          const bucketLabel = `${bucketStart}-${bucketEnd}`

          ageBuckets.set(bucketLabel, (ageBuckets.get(bucketLabel) || 0) + 1)
        }
      }
    })

    // Convert to chart data format
    chartData = Array.from(ageBuckets.entries())
      .map(([label, count]) => ({ label, value: label, count }))
      .sort((a, b) => {
        const aStart = parseInt(a.label.split('-')[0])
        const bStart = parseInt(b.label.split('-')[0])
        return aStart - bStart
      })
  }

  const colors = getChartColors()

  const chartConfig: ChartConfig = {
    count: {
      label: 'Count',
      color: colors[0],
    },
  }

  if (chartData.length === 0) {
    return (
      <ChartWrapper
        title={question.label}
        description={t("dashboard.charts.ages")}
      >
        <ChartEmptyState message={t('dashboard.charts.no_responses_yet')} />
      </ChartWrapper>
    )
  }

  return (
    <ChartWrapper
      title={question.label}
      description={t("dashboard.charts.ages_count", {
        count: totalResponses ||
          chartData.reduce((sum, item) => sum + item.count, 0)
      })}
      contentClassName="items-center"
    >
      <ChartContainer config={chartConfig} className="w-full h-[250px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 30, right: 50, }}
          >
            <XAxis
              type="category"
              dataKey="label"
              interval={0}
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis
              type="number"
              domain={[0, 'dataMax']}
              allowDecimals={false}
            />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Bar dataKey="count" fill={colors[0]} radius={10} barSize={40} />
          </BarChart>
        </ResponsiveContainer>
      </ChartContainer>
    </ChartWrapper>
  )
}
